import SectionHeading from '@/components/base/SectionHeading';
import ScrollReveal from '@/components/base/ScrollReveal';
import ArticleLink from '@/components/feature/ArticleLink';
import { riskCategories } from '@/mocks/riskManagementPage';

const spanClass: Record<number, string> = {
  4: 'lg:col-span-4',
  5: 'lg:col-span-5',
  6: 'lg:col-span-6',
  7: 'lg:col-span-7',
  12: 'lg:col-span-12',
};

const tone = {
  primary: {
    num: 'text-primary-600',
    chip: 'bg-primary-100 text-primary-800',
    rule: 'bg-primary-500',
    card: 'border-primary-200 bg-primary-50/40',
  },
  accent: {
    num: 'text-accent-700',
    chip: 'bg-accent-100 text-accent-900',
    rule: 'bg-accent-500',
    card: 'border-accent-200 bg-accent-50/50',
  },
  secondary: {
    num: 'text-secondary-700',
    chip: 'bg-secondary-100 text-secondary-900',
    rule: 'bg-secondary-500',
    card: 'border-background-200 bg-background-50',
  },
};

export default function TutorialLibrary() {
  const totalArticles = riskCategories.reduce((sum, category) => sum + category.articles.length, 0);

  return (
    <section
      id="tutorial-library"
      className="scroll-mt-[7.5rem] border-b border-background-200 bg-background-50 md:scroll-mt-[9rem]"
    >
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between lg:gap-12">
          <SectionHeading
            eyebrow="คลังบทความ"
            title="คลังบทความการบริหารความเสี่ยง"
            subtitle="เริ่มจากหัวข้อที่ตรงกับคำถามของคุณ แต่ละหมวดมีห้าบทความที่อ่านต่อได้ทันที ตั้งแต่เงินทุนและขนาดสถานะ ไปจนถึง Drawdown และวินัย"
          />
          <dl className="flex shrink-0 items-center gap-8 border-t border-background-200 pt-4 lg:border-0 lg:pt-0">
            <div>
              <dt className="text-xs text-foreground-500">หมวดบทความ</dt>
              <dd className="font-num text-2xl font-semibold text-foreground-950">{riskCategories.length}</dd>
            </div>
            <div>
              <dt className="text-xs text-foreground-500">บทความในคลัง</dt>
              <dd className="font-num text-2xl font-semibold text-foreground-950">{totalArticles}</dd>
            </div>
          </dl>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 md:mt-12 md:grid-cols-2 md:gap-5 lg:grid-cols-12">
          {riskCategories.map((category, index) => {
            const palette = tone[category.tone];
            const isFeatured = Boolean(category.featured);
            const isWide = category.colSpan === 12;

            return (
              <ScrollReveal
                as="article"
                key={category.id}
                delay={index * 60}
                className={`${spanClass[category.colSpan]} ${
                  isWide ? 'md:col-span-2' : ''
                } flex flex-col rounded-xl border p-5 md:p-6 ${palette.card}`}
              >
                <div id={`tutorial-${category.id}`} className="scroll-mt-[7.5rem] md:scroll-mt-[9rem]">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-baseline gap-3">
                      <span className={`font-num text-2xl font-semibold leading-none md:text-[28px] ${palette.num}`}>
                        {category.no}
                      </span>
                      <h3
                        className={`font-heading font-semibold leading-snug text-foreground-950 ${
                          isFeatured ? 'text-lg md:text-[22px]' : isWide ? 'text-lg md:text-xl' : 'text-base md:text-lg'
                        }`}
                      >
                        {category.title}
                      </h3>
                    </div>
                    <span
                      className={`mt-1 hidden shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold sm:inline-flex ${palette.chip}`}
                    >
                      {category.articles.length} บทความ
                    </span>
                  </div>

                  <p
                    className={`mt-3 text-[13px] leading-relaxed text-foreground-600 ${
                      isFeatured ? 'md:max-w-2xl' : isWide ? 'max-w-2xl' : ''
                    }`}
                  >
                    {category.intro}
                  </p>
                </div>

                <ul
                  className={`mt-5 flex flex-1 flex-col divide-y divide-background-200 border-t border-background-200 ${
                    isWide ? 'lg:grid lg:grid-cols-2 lg:gap-x-8 lg:divide-y-0' : ''
                  }`}
                >
                  {category.articles.map((article, articleIndex) => (
                    <li key={`${category.id}-${article.slug}-${articleIndex}`} className={isWide ? 'lg:border-t lg:border-background-200' : ''}>
                      <ArticleLink
                        to={`/${article.slug}`}
                        className="group/link flex items-start gap-3 py-3 pr-1 transition-colors"
                        aria-label={`เปิดบทความ ${article.title}`}
                      >
                        <span
                          className={`mt-0.5 shrink-0 font-num text-[11px] font-semibold ${
                            articleIndex === 0 ? palette.num : 'text-foreground-400'
                          }`}
                        >
                          {String(articleIndex + 1).padStart(2, '0')}
                        </span>
                        <span
                          className={`min-w-0 flex-1 leading-relaxed text-foreground-700 transition-colors group-hover/link:text-primary-700 ${
                            isFeatured ? 'text-[13px] md:text-sm' : 'text-[13px]'
                          }`}
                        >
                          {article.title}
                        </span>
                        <i
                          className="ri-arrow-right-s-line mt-0.5 shrink-0 text-base text-foreground-300 transition-colors group-hover/link:text-primary-600"
                          aria-hidden="true"
                        />
                      </ArticleLink>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 flex items-center gap-2 border-t border-background-200 pt-3.5">
                  <span className={`h-px w-6 ${palette.rule}`} aria-hidden="true" />
                  <span className="text-[11px] text-foreground-500">
                    ทั้งห้าบทความเปิดอ่านได้แล้ว กดชื่อบทความเพื่ออ่านฉบับเต็ม
                  </span>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        <div className="mt-6 flex items-start gap-2.5 rounded-lg border border-background-200 bg-background-100/70 p-4">
          <i className="ri-shield-check-line mt-0.5 text-base text-foreground-500" aria-hidden="true" />
          <p className="text-xs leading-relaxed text-foreground-600">
            ทั้ง {totalArticles} บทความในคลังอ้างอิงหลักการบริหารความเสี่ยงเท่านั้น ไม่มีการรับประกันผลตอบแทน
            และไม่ใช่คำแนะนำให้เปิดออเดอร์ตามบทความ
          </p>
        </div>
      </div>
    </section>
  );
}